import { useEffect } from "react";
import toast from "react-hot-toast";
import { socket as globalSocket } from "../../../config/socket.io";
import { playSound, stopSound } from "../../Soundmaker";
import {
  EVENTS,
  SETTLEMENT_PHASE_MS,
  FINISHED_ORDER_RETENTION_MS,
} from "../constants/upDownConstants";
import { formatUsd, normalizePairIdentity } from "../utils/upDownFormatters";

/**
 * Custom hook to manage WebSocket connection, subscriptions, and real-time events
 * for Up/Down binary options trading.
 */
export function useUpDownSocket({
  socket = globalSocket,
  socketRef,
  currentPairRoom,
  variantRef,
  chartRef,
  setLivePrice,
  setClockNow,
  setTickerOffset,
  setPhase,
  setPhaseStartedAt,
  setPhaseEndsAt,
  setRoundDetails,
  setRoundStartPrice,
  setSettlement,
  setRoundBidsRefreshTick,
  setActiveOrders,
  setLatestOrder,
  setResultModalQueue,
  setWallet,
  getWallet,
  getLuckyRoundDetails,
  activeRoundStartRef,
  currentRoundIdRef,
  startingPriceRef,
  settledOrderIdsRef,
  resultShownIdsRef,
  settledToastKeysRef,
  orderRemovalTimersRef,
  backendIdToLocalIdRef,
  justSettledTradeRef,
  chartOrderHydrationRef,
  seenSpinCountRef,
}) {
  useEffect(() => {
    if (!currentPairRoom) {
      return;
    }
    socket.connect();
    if (socketRef) {
      socketRef.current = socket;
    }

    const toPrettyJson = (value) => {
      try {
        return JSON.stringify(value, null, 2);
      } catch (error) {
        return String(value);
      }
    };

    const handleConnect = () => {
      socket.emit(EVENTS.subscribe, {
        pair: currentPairRoom,
        token: localStorage.getItem("olympusMons"),
      });
    };

    const handleConnectError = (error) => {
      console.error("Socket connection error:", error?.message || error);
    };

    const createEventLogger =
      (eventName) =>
      (...args) => {
        const payload = args.length <= 1 ? args[0] : args;
        // console.log(`[socket:${eventName}]`, toPrettyJson(payload));
      };

    const toEpochMs = (value) => {
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return null;
      }
      return parsed >= 1_000_000_000_000 ? parsed : parsed * 1000;
    };

    const toRoundStartMs = (payload) => {
      const fromRoundStart = toEpochMs(payload?.roundStart);
      if (fromRoundStart) {
        return fromRoundStart;
      }

      const roundIdRaw = String(payload?.roundId || "");
      const maybeTs = Number(roundIdRaw.split(":").pop());
      return toEpochMs(maybeTs);
    };

    const isCurrentPairPayload = (payload) => {
      const pair = String(payload?.pair || "").toUpperCase();
      return !pair || pair === currentPairRoom;
    };

    const handleRoundSnapshot = (data) => {
      if (!isCurrentPairPayload(data)) {
        return;
      }

      const incomingRoundStartMs = toRoundStartMs(data) ?? 0;
      if (
        activeRoundStartRef?.current > 0 &&
        incomingRoundStartMs > 0 &&
        incomingRoundStartMs < activeRoundStartRef.current
      ) {
        return;
      }

      if (typeof data?.roundId === "string" && data.roundId.length > 0) {
        if (currentRoundIdRef) currentRoundIdRef.current = data.roundId;
        if (incomingRoundStartMs > 0 && activeRoundStartRef) {
          activeRoundStartRef.current = incomingRoundStartMs;
        }
      }
      setRoundDetails((previous) => ({
        ...previous,
        roundId: String(data?.roundId || ""),
        openPrice: Number(data?.openPrice) || 0,
        status: String(data?.status || ""),
        pair: String(data?.pair || currentPairRoom || ""),
      }));
      const now = Date.now();
      const status = String(data?.status || "").toUpperCase();
      const roundStartMs = toEpochMs(data?.roundStart) ?? now;
      const lockTimeMs = toEpochMs(data?.lockTime);
      const roundEndMs = toEpochMs(data?.roundEnd);
      const openPrice = Number(data?.openPrice);
      const currentPrice = Number(data?.currentPrice);

      if (Number.isFinite(openPrice) && openPrice > 0) {
        setRoundStartPrice(openPrice);
      }
      if (Number.isFinite(currentPrice) && currentPrice > 0) {
        setLivePrice(currentPrice);
        const serverTs =
          toEpochMs(data?.ts) ??
          toEpochMs(data?.trade_time) ??
          toEpochMs(data?.sourceTimeMs) ??
          toEpochMs(data?.time);
        if (serverTs > 0) {
          setTickerOffset(serverTs - Date.now());
        }
      }

      if (status === "OPEN") {
        setPhase("trading");
        setSettlement(null);
        setClockNow(now);
        setPhaseStartedAt(roundStartMs);
        setPhaseEndsAt(
          lockTimeMs ?? now + variantRef.current.tradeWindowSeconds * 1000,
        );
        return;
      }

      if (status === "LOCKED") {
        setPhase("locked");
        setClockNow(now);
        setPhaseStartedAt(lockTimeMs ?? now);
        setPhaseEndsAt(
          roundEndMs ?? now + variantRef.current.lockWindowSeconds * 1000,
        );
        return;
      }

      if (status === "SETTLED") {
        setPhase("settled");
        setClockNow(now);
        setPhaseStartedAt(roundEndMs ?? now);
        setPhaseEndsAt(now + SETTLEMENT_PHASE_MS);
      }
    };

    const handleBinaryTradeResult = (tradeResult) => {
      if (tradeResult?.success) {
        // Immediately update wallet and hasSpin from tradeResult payload without waiting for HTTP getWallet
        if (tradeResult?.hasSpin) {
          setWallet((previous) => {
            const next = {
              ...previous,
              hasSpin: tradeResult.hasSpin,
              balance: tradeResult.walletBalance !== undefined ? tradeResult.walletBalance : previous?.balance,
            };
            if (next.spin_and_win) {
              next.spin_and_win = {
                ...next.spin_and_win,
                hasSpin: tradeResult.hasSpin,
              };
            }
            return next;
          });

          // Trigger lucky wheel popup ONLY if this settlement crossed another point milestone (e.g. hit another 100 points)
          const currentSpinCount = Number(tradeResult.hasSpin?.spinCount || 0);
          const hasEarnedNewFlag = Boolean(tradeResult.hasSpin?.earnedNewSpin);
          const hasIncreasedCount = Boolean(seenSpinCountRef?.current !== null && currentSpinCount > (seenSpinCountRef?.current ?? 0));
          const earnedNew = hasEarnedNewFlag || hasIncreasedCount;

          console.log("[LuckyWheelTrigger] tradeResult hasSpin:", tradeResult.hasSpin, "showSpin(legacy):", tradeResult.showSpin, "earnedNew:", earnedNew, "currentSpinCount:", currentSpinCount, "seenSpinCount:", seenSpinCountRef?.current);
          if (seenSpinCountRef) seenSpinCountRef.current = currentSpinCount;

          if (earnedNew) {
            if (typeof window !== "undefined") {
              console.log("[LuckyWheelTrigger] 🎯 Dispatching 'open-lucky-wheel' event!");
              window.dispatchEvent(new CustomEvent("open-lucky-wheel", { detail: { variant: "spin" } }));
            }
          }
        }

        const result = tradeResult.result;
        stopSound("countdown");
        
        const resultPair = normalizePairIdentity(
          result?.symbol || result?.pair,
        );
        const activePair = normalizePairIdentity(currentPairRoom);
        if (resultPair && activePair && resultPair !== activePair) return;

        // Remove the settled trade from activeOrders so the "max 3" check
        // doesn't block new trades after the backend has already closed them.
        const backendId = String(result?.id || "").trim();
        const localId = backendIdToLocalIdRef?.current?.get(backendId);
        const settledId = result?.clientId || localId || backendId;

        // Show result modal only if not already shown by chart settlement
        const resultDedupId = settledId || backendId;
        if (!resultShownIdsRef?.current?.has(resultDedupId)) {
          if (resultShownIdsRef?.current) {
            resultShownIdsRef.current.add(resultDedupId);
            setTimeout(() => resultShownIdsRef.current.delete(resultDedupId), 10000);
          }
          setResultModalQueue((previous) => [
            ...previous,
            {
              id: result?.id,
              side: result?.side,
              entryPrice: result?.entryPrice,
              exitPrice: result?.exitPrice,
              amount: result?.amount,
              profit: result?.profit,
              status: result?.status,
            },
          ]);

          // Play sound based on authoritative backend result
          if (result?.status === "won") {
            playSound("win");
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("ez5050-settlement-aura", { detail: { outcome: "won" } }));

              // Track consecutive win streak
              let currentStreak = 1;
              try {
                const prevStreak = parseInt(sessionStorage.getItem("ez5050_win_streak") || "0", 10);
                currentStreak = (isNaN(prevStreak) ? 0 : prevStreak) + 1;
                sessionStorage.setItem("ez5050_win_streak", String(currentStreak));
              } catch (_e) {}

              // Check if round was played with combo multiplier
              const wasLuckyRound = typeof sessionStorage !== "undefined" && sessionStorage.getItem("ez5050_last_order_lucky") === "true";
              const isComboRound = Boolean(
                wasLuckyRound ||
                result?.isLuckyRound ||
                Number(result?.multiplier) > 1 ||
                Number(result?.jackpot_multiplier) > 1
              );
              try {
                sessionStorage.removeItem("ez5050_last_order_lucky");
              } catch (_e) {}

              // Trigger Critical Win if:
              // 1. At least 3 consecutive wins (currentStreak >= 3), OR
              // 2. On combo multiplier rounds
              if (currentStreak >= 3 || isComboRound) {
                const rawProfit = Number(result?.profit);
                const winAmount = Number.isFinite(rawProfit) && rawProfit !== 0
                  ? Math.abs(rawProfit)
                  : Number(result?.amount) || 0;
                window.dispatchEvent(new CustomEvent("ez5050-critical-win", {
                  detail: {
                    amount: winAmount,
                    streak: currentStreak,
                    isComboRound,
                  }
                }));
              }
            }
          } else if (result?.status === "loss" || result?.status === "lost" || result?.status === "lose") {
            playSound("lose");
            try {
              sessionStorage.setItem("ez5050_win_streak", "0");
              sessionStorage.removeItem("ez5050_last_order_lucky");
            } catch (_e) {}
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("ez5050-settlement-aura", { detail: { outcome: "loss" } }));
            }
          }

          // Screen shake on settlement: ONLY on loss, only once
          const isLoss = result?.status === "loss" || result?.status === "lost" || result?.status === "lose";
          if (isLoss) {
            const chartStage = document.querySelector(".updown-chart-stage");
            if (chartStage) {
              chartStage.classList.remove("chart-shake-loss", "chart-shake-win");
              void chartStage.offsetWidth;
              chartStage.classList.add("chart-shake-loss");
              setTimeout(() => chartStage.classList.remove("chart-shake-loss"), 500);
            }
          }

          // Auto-remove from queue after RPG animation completes (2.5s)
          setTimeout(() => {
            setResultModalQueue((previous) => previous.filter(r => r.id !== result?.id));
          }, 2500);
        }

        // Refresh lucky/combo multiplier data on socket trade result
        // Only clear seen key if trade was a win (losing trades never earn combos and must not re-trigger modals)
        const isTradeWin = result?.status === "won" || tradeResult?.status === "won";
        if (isTradeWin) {
          if (justSettledTradeRef) justSettledTradeRef.current = true;
          try {
            sessionStorage.removeItem("seen_lucky_combo_key");
          } catch (_e) {}
        }
        if (typeof getLuckyRoundDetails === "function") {
          getLuckyRoundDetails();
        }
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("refresh-lucky-details"));
        }

        if (settledId) {
          // Track this ID so getWallet responses don't re-add it
          if (settledOrderIdsRef?.current) {
            settledOrderIdsRef.current.add(settledId);
            setTimeout(() => settledOrderIdsRef.current.delete(settledId), 30000);
          }

          // Update activeOrders and latestOrder to reflect authoritative result immediately
          setActiveOrders((previous) =>
            previous?.map((order) =>
              (order.id === settledId || order.id === backendId)
                ? {
                    ...order,
                    status: result?.status,
                    won: result?.status === "won",
                    profit: result?.profit,
                    exitPrice: result?.exitPrice,
                  }
                : order
            )
          );
          setLatestOrder((previous) =>
            (previous?.id === settledId || previous?.id === backendId)
              ? {
                  ...previous,
                  status: result?.status,
                  won: result?.status === "won",
                  profit: result?.profit,
                  exitPrice: result?.exitPrice,
                }
              : previous
          );

          const existingTimer = orderRemovalTimersRef?.current?.get(settledId);
          if (existingTimer) {
            clearTimeout(existingTimer);
          }
          const removalTimer = setTimeout(() => {
            setActiveOrders((previous) =>
              previous?.filter((order) => order.id !== settledId && order.id !== backendId),
            );
            setLatestOrder((previous) =>
              (previous?.id === settledId || previous?.id === backendId) ? null : previous,
            );
            chartRef.current?.removeOrder?.(settledId);
            chartRef.current?.removeOrder?.(backendId);
            if (chartOrderHydrationRef?.current) {
              chartOrderHydrationRef.current.delete(settledId);
              chartOrderHydrationRef.current.delete(backendId);
            }
            if (orderRemovalTimersRef?.current) {
              orderRemovalTimersRef.current.delete(settledId);
            }
          }, FINISHED_ORDER_RETENTION_MS);
          if (orderRemovalTimersRef?.current) {
            orderRemovalTimersRef.current.set(settledId, removalTimer);
          }
        }
      }
      if (typeof getWallet === "function") {
        getWallet();
      }
    };

    const handleRoundStarted = (data) => {
      try {
        console.log("[socket:roundStarted]", toPrettyJson(data));
        if (!isCurrentPairPayload(data)) {
          return;
        }

        const incomingRoundStartMs = toRoundStartMs(data) ?? 0;
        if (
          activeRoundStartRef?.current > 0 &&
          incomingRoundStartMs > 0 &&
          incomingRoundStartMs < activeRoundStartRef.current
        ) {
          return;
        }

        if (typeof data?.roundId === "string" && data.roundId.length > 0) {
          if (currentRoundIdRef) currentRoundIdRef.current = data.roundId;
          if (incomingRoundStartMs > 0 && activeRoundStartRef) {
            activeRoundStartRef.current = incomingRoundStartMs;
          }
        }

        setRoundDetails((previous) => ({
          ...previous,
          roundId: String(data?.roundId || previous?.roundId || ""),
          pair: String(data?.pair || currentPairRoom || previous?.pair || ""),
          status: String(data?.status || previous?.status || ""),
          openPrice: Number(data?.openPrice) || previous?.openPrice || 0,
          stats: {},
        }));
        const now = Date.now();
        const openPrice = Number(data?.openPrice);
        const roundStartMs = toEpochMs(data?.roundStart) ?? now;
        const lockTimeMs =
          toEpochMs(data?.lockTime) ??
          toEpochMs(data?.roundEnd) ??
          now + variantRef.current.tradeWindowSeconds * 1000;

        if (Number.isFinite(openPrice) && openPrice > 0) {
          setRoundStartPrice(openPrice);
          setLivePrice(openPrice);
        }

        setPhase("trading");
        setSettlement(null);
        setClockNow(now);
        setPhaseStartedAt(roundStartMs);
        setPhaseEndsAt(lockTimeMs);
      } catch (error) {
        if (startingPriceRef?.current) {
          setRoundStartPrice(startingPriceRef.current);
          setLivePrice(startingPriceRef.current);
        }
        setPhase("trading");
        setSettlement(null);
        setClockNow(Date.now());
        setPhaseStartedAt(Date.now());
        setPhaseEndsAt(
          Date.now() + variantRef.current.tradeWindowSeconds * 1000,
        );
      }
    };

    const handleRoundLocked = (data) => {
      if (!isCurrentPairPayload(data)) {
        return;
      }

      const now = Date.now();
      const lockedAtMs = toEpochMs(data?.lockedAt) ?? now;
      const roundEndMs =
        toEpochMs(data?.roundEnd) ??
        lockedAtMs + variantRef.current.lockWindowSeconds * 1000;

      setPhase("locked");
      stopSound("countdown");
      setClockNow(now);
      setPhaseStartedAt(lockedAtMs);
      setPhaseEndsAt(roundEndMs);
    };

    const handleRoundSettled = (data) => {
      // reserved for future explicit round-settled payloads
    };

    const handleBidAccepted = (data) => {
      setWallet((previous) => ({
        ...previous,
        balance: data?.walletBalance,
      }));
      createEventLogger(EVENTS.bidAccepted)(data);
    };

    const handleBidRejected = (data) => {
      createEventLogger(EVENTS.bidRejected)(data);
      toast.error("Cannot place trade");
    };

    const handleRoundStatsUpdated = (data) => {
      if (!isCurrentPairPayload(data)) {
        return;
      }

      const nextRoundId = String(data?.roundId || "").trim();
      if (nextRoundId && currentRoundIdRef) {
        currentRoundIdRef.current = nextRoundId;
      }

      setRoundDetails((previous) => ({
        ...previous,
        roundId: nextRoundId || previous.roundId,
        pair: String(data?.pair || previous.pair || currentPairRoom),
        stats: data?.stats ? data.stats : {},
      }));
      setRoundBidsRefreshTick((previous) => previous + 1);
    };

    const handleMyBidSettled = (data) => {
      createEventLogger(EVENTS.myBidSettled)(data);
      const outcome = String(data?.outcome || data?.status || "").toUpperCase();
      const settlementKey = [
        String(data?.bidId || ""),
        String(data?.roundId || ""),
        String(data?.pair || currentPairRoom || ""),
        String(data?.direction || ""),
        String(Number(data?.amount) || 0),
        outcome,
        String(Number(data?.payout) || 0),
        String(data?.placedAt || data?.settledAt || ""),
        String(data?.user?.userId || data?.userId || ""),
      ].join("|");

      if (settledToastKeysRef?.current?.has(settlementKey)) {
        return;
      }
      if (settledToastKeysRef?.current) {
        settledToastKeysRef.current.add(settlementKey);
        setTimeout(() => {
          settledToastKeysRef.current.delete(settlementKey);
        }, 45000);
      }

      const toastId = `settled:${settlementKey}`;
      if (outcome === "WON" || outcome === "WIN") {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("ez5050-settlement-aura", { detail: { outcome: "won" } }));

          let currentStreak = 1;
          try {
            const prevStreak = parseInt(sessionStorage.getItem("ez5050_win_streak") || "0", 10);
            currentStreak = (isNaN(prevStreak) ? 0 : prevStreak) + 1;
            sessionStorage.setItem("ez5050_win_streak", String(currentStreak));
          } catch (_e) {}

          const wasLuckyRound = typeof sessionStorage !== "undefined" && sessionStorage.getItem("ez5050_last_order_lucky") === "true";
          const isComboRound = Boolean(
            wasLuckyRound ||
            data?.isLuckyRound ||
            Number(data?.multiplier) > 1 ||
            Number(data?.jackpot_multiplier) > 1
          );
          try {
            sessionStorage.removeItem("ez5050_last_order_lucky");
          } catch (_e) {}

          if (currentStreak >= 3 || isComboRound) {
            window.dispatchEvent(new CustomEvent("ez5050-critical-win", {
              detail: {
                amount: data?.payout || data?.profit || 0,
                streak: currentStreak,
                isComboRound,
              }
            }));
          }
        }
        toast.success(
          `Trade settled: WON (${formatUsd(Number(data?.payout) || 0)})`,
          { id: toastId },
        );
        return;
      }
      if (outcome === "LOST" || outcome === "LOSS") {
        try {
          sessionStorage.setItem("ez5050_win_streak", "0");
          sessionStorage.removeItem("ez5050_last_order_lucky");
        } catch (_e) {}
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("ez5050-settlement-aura", { detail: { outcome: "loss" } }));
        }
        toast.error("Trade settled: LOST", { id: toastId });
        return;
      }
      toast("Trade settled", { id: toastId });
    };

    const handlePriceTick = (data) => {
      if (!isCurrentPairPayload(data)) {
        return;
      }
      const price = Number(data?.price);
      if (!Number.isFinite(price) || price <= 0) {
        return;
      }
      const serverTs =
        Number(
          data?.sourceTimeMs ??
            (data?.ts
              ? data.ts >= 1_000_000_000_000
                ? data.ts
                : data.ts * 1000
              : 0),
        ) || Number(data?.time) * 1000;
      setLivePrice(price);
      if (serverTs > 0) {
        setTickerOffset(serverTs - Date.now());
      }

      chartRef.current?.pushExternalTick?.({
        price,
        time: Math.floor((data?.ts || serverTs || Date.now()) / 1000),
      });
    };

    const handleAnyEvent = (event, ...args) => {
      if (event === EVENTS.priceTick) {
        return;
      }
    };

    const withSocketTryCatch =
      (eventName, handler) =>
      (...args) => {
        try {
          handler(...args);
        } catch (error) {
          const payload = args.length <= 1 ? args[0] : args;
          console.error(
            `[socket:${eventName}] listener error:`,
            error?.message || error,
            payload,
          );
        }
      };

    const onConnect = withSocketTryCatch("connect", handleConnect);
    const onConnectError = withSocketTryCatch(
      "connect_error",
      handleConnectError,
    );
    const onRoundSnapshot = withSocketTryCatch(
      EVENTS.roundSnapshot,
      handleRoundSnapshot,
    );
    const onRoundStarted = withSocketTryCatch(
      EVENTS.roundStarted,
      handleRoundStarted,
    );
    const onRoundLocked = withSocketTryCatch(
      EVENTS.roundLocked,
      handleRoundLocked,
    );
    const onRoundSettled = withSocketTryCatch(
      EVENTS.roundSettled,
      handleRoundSettled,
    );
    const onBidAccepted = withSocketTryCatch(
      EVENTS.bidAccepted,
      handleBidAccepted,
    );
    const onBidRejected = withSocketTryCatch(
      EVENTS.bidRejected,
      handleBidRejected,
    );
    const onRoundStatsUpdated = withSocketTryCatch(
      EVENTS.roundStatsUpdated,
      handleRoundStatsUpdated,
    );
    const onMyBidSettled = withSocketTryCatch(
      EVENTS.myBidSettled,
      handleMyBidSettled,
    );
    const onPriceTick = withSocketTryCatch(EVENTS.priceTick, handlePriceTick);
    const onAnyEvent = withSocketTryCatch("onAny", handleAnyEvent);

    socket.on("connect", onConnect);
    socket.on("connect_error", onConnectError);
    socket.on(EVENTS.roundSnapshot, onRoundSnapshot);
    socket.on(EVENTS.roundStarted, onRoundStarted);
    socket.on(EVENTS.roundLocked, onRoundLocked);
    socket.on(EVENTS.roundSettled, onRoundSettled);
    socket.on(EVENTS.bidAccepted, onBidAccepted);
    socket.on(EVENTS.bidRejected, onBidRejected);
    socket.on(EVENTS.roundStatsUpdated, onRoundStatsUpdated);
    socket.on(EVENTS.myBidSettled, onMyBidSettled);
    socket.on(EVENTS.priceTick, onPriceTick);
    socket.onAny(onAnyEvent);

    socket.on("binaryTradeResult", handleBinaryTradeResult);

    return () => {
      if (socket.connected) {
        socket.emit(EVENTS.unsubscribe, { pair: currentPairRoom });
      }
      socket.off("connect", onConnect);
      socket.off("connect_error", onConnectError);
      socket.off(EVENTS.roundSnapshot, onRoundSnapshot);
      socket.off(EVENTS.roundStarted, onRoundStarted);
      socket.off(EVENTS.roundLocked, onRoundLocked);
      socket.off(EVENTS.roundSettled, onRoundSettled);
      socket.off(EVENTS.bidAccepted, onBidAccepted);
      socket.off(EVENTS.bidRejected, onBidRejected);
      socket.off(EVENTS.roundStatsUpdated, onRoundStatsUpdated);
      socket.off(EVENTS.myBidSettled, onMyBidSettled);
      socket.off(EVENTS.priceTick, onPriceTick);
      socket.offAny(onAnyEvent);
      socket.off("binaryTradeResult", handleBinaryTradeResult);
      if (socketRef) {
        socketRef.current = null;
      }
    };
  }, [currentPairRoom, socket]);
}

